import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon } from '@/components/Icon'
import { ConfirmModal, StateView, type MenuAction } from '@/card/record'
import { formatNumber } from '@/lib/format'
import { AdminState, Notice, RowMenu, SkeletonTable } from '@/admin/AdminShell'
import { AccessDenied } from '@/pages/AccessDenied'
import type { WarehouseList, ZoneRow } from './topologyApi'
import { deleteWarehouse, deleteZone, fetchWarehouse, fetchWarehouses } from './topologyApi'
import { RESOURCE, WAREHOUSES_KEY, WarehouseSheet, editDraft, type WarehouseDraft } from './WarehousesPage'
import { ZoneSheet, newZoneDraft, zoneDraft, type ZoneDraft } from './ZoneSheet'
import { CellsPanel } from './CellsPanel'
import '@/styles/admin.css'

/**
 * Страница склада: шапка с реквизитами, зоны и ячейки выбранной зоны.
 *
 * Зона — уровень, где решается режим хранения (ТЗ §4.5): один склад держит стеллажи,
 * напольное хранение и ответственное хранение одновременно. Ячейки есть только у адресной
 * зоны — у остальных вместо таблицы ячеек сказано словами, что режим их не использует.
 *
 * Удаление — через подтверждение, и сервер откажет, если на складе или в зоне лежит товар
 * (`topology.*_in_use`): отказ показывается его словами, а не общей «ошибкой».
 *
 * Кнопки правки — по флагам склада с сервера (`editable`, `deletable`, `extendable`): склад
 * чужого заведующего виден, но не правится (V59), и правило здесь не повторяется.
 */
export function WarehouseTopologyPage() {
  const { id = '' } = useParams()
  const { t, locale, user } = useApp()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const mayRead = can(user, RESOURCE, 'read')
  const detail = useQuery({ queryKey: [...WAREHOUSES_KEY, id], enabled: mayRead, queryFn: ({ signal }) => fetchWarehouse(id, signal) })
  // Справочники форм (режимы, стратегии, организации) приходят со списком складов.
  const dictionaries = useQuery({ queryKey: WAREHOUSES_KEY, enabled: mayRead, queryFn: ({ signal }) => fetchWarehouses(signal) })
  const [zoneId, setZoneId] = useState<string | null>(null)
  const [zoneEdit, setZoneEdit] = useState<ZoneDraft | null>(null)
  const [warehouseEdit, setWarehouseEdit] = useState<WarehouseDraft | null>(null)
  const [removingZone, setRemovingZone] = useState<ZoneRow | null>(null)
  const [removingSite, setRemovingSite] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: WAREHOUSES_KEY })
  }
  const fail = (error: unknown) => setNotice(error instanceof Error ? error.message : t('app.error'))

  const removeZone = useMutation({
    mutationFn: (zone: ZoneRow) => deleteZone(zone.id),
    onSuccess: (_, zone) => {
      setRemovingZone(null)
      if (zoneId === zone.id) setZoneId(null)
      refresh()
    },
    onError: (error) => { setRemovingZone(null); fail(error) },
  })
  const removeSite = useMutation({
    mutationFn: () => deleteWarehouse(id),
    onSuccess: () => { refresh(); navigate('/warehouses') },
    onError: (error) => { setRemovingSite(false); fail(error) },
  })

  // Прямая ссылка минует меню: без права экран говорит «нет доступа», а не крутит загрузку.
  if (!mayRead) return <AccessDenied />

  const site = detail.data?.warehouse
  const mayCreate = site?.extendable ?? false
  const mayEdit = site?.editable ?? false
  const mayDelete = site?.deletable ?? false
  const zones = detail.data?.zones ?? []
  const selected = zones.find((zone) => zone.id === zoneId) ?? zones.find((zone) => zone.usesCells) ?? zones[0] ?? null
  const dict: WarehouseList | undefined = dictionaries.data

  return (
    <>
      <nav className="crumbs" aria-label={t('record.crumbs')}>
        <span>{t('nav.section.warehouse')}</span>
        <span aria-hidden="true">/</span>
        <Link to="/warehouses">{t('nav.warehouses')}</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{site?.code ?? '…'}</span>
      </nav>
      <AdminState
        isLoading={detail.isLoading}
        error={detail.error}
        onRetry={() => void detail.refetch()}
        forbiddenTitleKey="topo.forbidden"
        errorTitleKey="topo.error"
        skeleton={<SkeletonTable columns={6} rows={4} />}
      >
        {site && (
          <>
            <div className="page-head">
              <h1 className="h1">{site.name}</h1>
              <span className="mono muted t-md">{site.code}</span>
              {!site.active && <span className="badge">{t('topo.inactive')}</span>}
              <span className="spacer" />
              {mayDelete && (
                <button type="button" className="btn btn-ghost" onClick={() => setRemovingSite(true)}>
                  <Icon name="trash" />{t('topo.wh.delete')}
                </button>
              )}
              {mayEdit && (
                <button type="button" className="btn btn-secondary" onClick={() => setWarehouseEdit(editDraft(site))}>
                  <Icon name="edit" />{t('admin.edit')}
                </button>
              )}
            </div>
            <p className="page-lead">
              {[site.organizationName, site.address, site.managerName && t('topo.wh.managerIs', { name: site.managerName }),
                t('topo.wh.strategyIs', { strategy: site.defaultPickingStrategy }),
                site.allowsCrossDocking ? t('topo.wh.crossDockShort') : null].filter(Boolean).join(' · ')}
            </p>
            {!mayEdit && !mayCreate && (
              <div className="ro-banner" role="note">
                <Icon name="lock" />
                <span><span className="strong">{t('adm.ro.title')}</span> {t('topo.ro')}</span>
              </div>
            )}
            <Notice text={notice} />

            <section className="panel" aria-labelledby="topo-zones-h" style={{ marginBottom: 16 }}>
              <div className="panel-h">
                <span className="h2" id="topo-zones-h">{t('topo.zones')}</span>
                <span className="count">{zones.length}</span>
                <span className="spacer" />
                {mayCreate && dict && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setZoneEdit(newZoneDraft(zones))}>
                    <Icon name="plus" size="sm" />{t('topo.zone.add')}
                  </button>
                )}
              </div>
              {zones.length === 0 ? (
                <StateView kind="setup" compact title={t('topo.zone.setup')} description={t('topo.zone.setup.text')} />
              ) : (
                <div className="tbl-wrap">
                  <table className="tbl" aria-label={t('topo.zones')}>
                    <thead>
                      <tr>
                        <th>{t('topo.zone.code')}</th>
                        <th>{t('topo.zone.name')}</th>
                        <th>{t('topo.zone.mode')}</th>
                        <th>{t('topo.zone.regime')}</th>
                        <th>{t('topo.zone.strategy')}</th>
                        <th className="num">{t('topo.zone.priority')}</th>
                        <th className="num">{t('topo.wh.cells')}</th>
                        {(mayEdit || mayDelete) && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {zones.map((zone) => {
                        const items: MenuAction[] = []
                        if (mayEdit) items.push({ id: 'edit', label: t('admin.edit'), icon: 'adm-edit', onSelect: () => setZoneEdit(zoneDraft(zone)) })
                        if (mayDelete) items.push({
                          id: 'delete', label: t('topo.zone.delete'), icon: 'trash', danger: true,
                          onSelect: () => setRemovingZone(zone),
                          disabledReason: zone.occupiedCells > 0 ? t('topo.zone.deleteBlocked', { count: zone.occupiedCells }) : undefined,
                        })
                        return (
                          <tr key={zone.id} className={selected?.id === zone.id ? 'is-selected' : undefined}>
                            <td>
                              <button type="button" className="btn btn-ghost btn-sm mono strong" aria-pressed={selected?.id === zone.id}
                                      onClick={() => setZoneId(zone.id)}>
                                {zone.code}
                              </button>
                            </td>
                            <td>
                              <div className="stack" style={{ gap: 2 }}>
                                <span>{zone.name}</span>
                                {zone.ownerName && <span className="t-xs muted">{t('topo.zone.ownerIs', { name: zone.ownerName })}</span>}
                              </div>
                              {!zone.active && <span className="badge" style={{ marginLeft: 6 }}>{t('topo.inactive')}</span>}
                            </td>
                            <td>{t(`topo.mode.${zone.storageMode}`)}</td>
                            <td>
                              {t(`topo.regime.${zone.temperatureRegime}`)}
                              {(zone.temperatureMinC !== undefined || zone.temperatureMaxC !== undefined) && (
                                <span className="t-xs muted"> {range(zone.temperatureMinC, zone.temperatureMaxC, locale)}</span>
                              )}
                            </td>
                            <td>
                              <span className="mono">{zone.effectivePickingStrategy}</span>
                              {!zone.pickingStrategy && <span className="t-xs muted"> {t('topo.zone.inherited')}</span>}
                            </td>
                            <td className="num">{zone.priority}</td>
                            <td className="num">
                              {zone.usesCells
                                ? t('topo.cells.summary', { cells: zone.cells, occupied: zone.occupiedCells, blocked: zone.blockedCells })
                                : <span className="muted">{t('topo.zone.noCells')}</span>}
                            </td>
                            {(mayEdit || mayDelete) && (
                              <td className="adm-act"><RowMenu label={t('topo.zone.actions', { code: zone.code })} items={items} /></td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {selected && (
              selected.usesCells ? (
                <CellsPanel zone={selected} maxCells={dict?.maxCells ?? 5000} defaultMask={dict?.defaultMask ?? '{ROW}-{RACK:2}-{TIER:2}-{POS}'}
                            mayCreate={mayCreate} mayEdit={mayEdit} mayDelete={mayDelete} onChanged={refresh} />
              ) : (
                <StateView boxed kind="empty" title={t('topo.cells.title', { zone: selected.code })}
                           description={t('topo.zone.noCells.text', { mode: t(`topo.mode.${selected.storageMode}`) })} />
              )
            )}
          </>
        )}
      </AdminState>

      {warehouseEdit && dict && (
        <WarehouseSheet draft={warehouseEdit} organizations={dict.organizations} strategies={dict.strategies}
                        mayAssignManager={dict.mayAssignManager} onChange={setWarehouseEdit} onClose={() => setWarehouseEdit(null)} />
      )}
      {zoneEdit && dict && (
        <ZoneSheet warehouseId={id} draft={zoneEdit} dictionaries={dict} warehouseStrategy={site?.defaultPickingStrategy ?? 'FIFO'}
                   onChange={setZoneEdit} onClose={() => setZoneEdit(null)} onSaved={refresh} />
      )}
      <ConfirmModal
        open={removingZone != null}
        title={t('topo.zone.deleteConfirm', { code: removingZone?.code ?? '' })}
        confirmLabel={t('topo.zone.delete')}
        pending={removeZone.isPending}
        onCancel={() => setRemovingZone(null)}
        onConfirm={() => removingZone && removeZone.mutate(removingZone)}
      >
        <p>{t('topo.zone.deleteConfirm.text', { count: removingZone?.cells ?? 0 })}</p>
      </ConfirmModal>
      <ConfirmModal
        open={removingSite}
        title={t('topo.wh.deleteConfirm', { code: site?.code ?? '' })}
        confirmLabel={t('topo.wh.delete')}
        pending={removeSite.isPending}
        onCancel={() => setRemovingSite(false)}
        onConfirm={() => removeSite.mutate()}
      >
        <p>{t('topo.wh.deleteConfirm.text')}</p>
      </ConfirmModal>
    </>
  )
}

function range(min: number | undefined, max: number | undefined, locale: Parameters<typeof formatNumber>[1]): string {
  const show = (value: number | undefined) => (value === undefined ? '…' : `${formatNumber(value, locale)}°`)
  return `${show(min)} — ${show(max)}`
}
